import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { AuthProvider } from "@/hooks/useAuth";
import Layout from "@/components/layout/Layout";
import Index from "./pages/Index";

import ScrollToTop from "./components/ScrollToTop";
import AccessibilityWidget from "./components/AccessibilityWidget";
import InstallBanner from "./components/InstallBanner";

// Lazy-loaded public pages
const About = lazy(() => import("./pages/About"));
const Recipes = lazy(() => import("./pages/Recipes"));
const Cookbooks = lazy(() => import("./pages/Cookbooks"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const RecipeDetail = lazy(() => import("./pages/RecipeDetail"));
const Events = lazy(() => import("./pages/Events"));
const Press = lazy(() => import("./pages/Press"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LinksPage = lazy(() => import("./pages/Links"));
const WalmartRedirect = lazy(() => import("./pages/WalmartRedirect"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const AccessibilityStatement = lazy(() => import("./pages/AccessibilityStatement"));
const DmcaPolicy = lazy(() => import("./pages/DmcaPolicy"));
const DataRequest = lazy(() => import("./pages/DataRequest"));
const Install = lazy(() => import("./pages/Install"));
const RssFeed = lazy(() => import("./pages/RssFeed"));
const Subscribe = lazy(() => import("./pages/Subscribe"));
const RecipeAlerts = lazy(() => import("./pages/RecipeAlerts"));
const RecipeRequest = lazy(() => import("./pages/RecipeRequest"));
const Contact = lazy(() => import("./pages/Contact"));
const Shop = lazy(() => import("./pages/Shop"));
const CategoryHub = lazy(() => import("./pages/CategoryHub"));
const RecipeIndex = lazy(() => import("./pages/RecipeIndex"));
const SearchResults = lazy(() => import("./pages/SearchResults"));
const PrintRecipe = lazy(() => import("./pages/PrintRecipe"));
const AffiliateDisclosure = lazy(() => import("./pages/AffiliateDisclosure"));
const EditorialPolicy = lazy(() => import("./pages/EditorialPolicy"));
const StartHere = lazy(() => import("./pages/StartHere"));

// Lazy-loaded admin pages
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminRoute = lazy(() => import("./components/admin/AdminRoute"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminBlogPosts = lazy(() => import("./pages/admin/AdminBlogPosts"));
const AdminBlogEditor = lazy(() => import("./pages/admin/AdminBlogEditor"));
const AdminBlogBulkImport = lazy(() => import("./pages/admin/AdminBlogBulkImport"));
const AdminRecipes = lazy(() => import("./pages/admin/AdminRecipes"));
const AdminCoverMaker = lazy(() => import("./pages/admin/AdminCoverMaker"));
const AdminRecipeEditor = lazy(() => import("./pages/admin/AdminRecipeEditor"));
const AdminRecipeBulkImport = lazy(() => import("./pages/admin/AdminRecipeBulkImport"));
const AdminCalendar = lazy(() => import("./pages/admin/AdminCalendar"));
const AdminInbox = lazy(() => import("./pages/admin/AdminInbox"));
const AdminHealthCheck = lazy(() => import("./pages/admin/AdminHealthCheck"));
const AdminMedia = lazy(() => import("./pages/admin/AdminMedia"));
const AdminEvents = lazy(() => import("./pages/admin/AdminEvents"));
const AdminPress = lazy(() => import("./pages/admin/AdminPress"));
const AdminInquiries = lazy(() => import("./pages/admin/AdminInquiries"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminComments = lazy(() => import("./pages/admin/AdminComments"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
const AdminLinks = lazy(() => import("./pages/admin/AdminLinks"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminNewsletterPreview = lazy(() => import("./pages/admin/AdminNewsletterPreview"));
const AdminContactSubmissions = lazy(() => import("./pages/admin/AdminContactSubmissions"));
const AdminRecipeAlerts = lazy(() => import("./pages/admin/AdminRecipeAlerts"));
const AdminSignupAttempts = lazy(() => import("./pages/admin/AdminSignupAttempts"));
const AdminRecipeRequests = lazy(() => import("./pages/admin/AdminRecipeRequests"));
const AdminTourInterest = lazy(() => import("./pages/admin/AdminTourInterest"));
const AdminShop = lazy(() => import("./pages/admin/AdminShop"));
const AdminCategories = lazy(() => import("./pages/admin/AdminCategories"));
const AdminSeoHealth = lazy(() => import("./pages/admin/AdminSeoHealth"));
const AdminBacklinks = lazy(() => import("./pages/admin/AdminBacklinks"));
const AdminCookbookVideos = lazy(() => import("./pages/admin/AdminCookbookVideos"));
const AdminCookbookVideoEditor = lazy(() => import("./pages/admin/AdminCookbookVideoEditor"));
const CookbookVideo = lazy(() => import("./pages/CookbookVideo"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 60 * 1000,
      gcTime: 60 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

const RSS_URL = `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID || 'ccpgzpcvxrjefmrguyks'}.supabase.co/functions/v1/rss-feed`;

const RssRedirect = () => {
  useEffect(() => { window.location.href = RSS_URL; }, []);
  return null;
};

const GlobalWidgets = () => {
  const isAdminRoute = window.location.pathname.startsWith("/daddy");
  if (isAdminRoute) return null;

  return (
    <>
      <AccessibilityWidget />
      <InstallBanner />
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <GlobalWidgets />
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Layout><Index /></Layout>} />
              <Route element={<LinksPage />} path="/links" />

              {/* Legal pages */}
              <Route path="/privacy-policy" element={<Layout><PrivacyPolicy /></Layout>} />
              <Route path="/terms" element={<Layout><Terms /></Layout>} />
              <Route path="/cookie-policy" element={<Layout><CookiePolicy /></Layout>} />
              <Route path="/accessibility" element={<Layout><AccessibilityStatement /></Layout>} />
              <Route path="/dmca" element={<Layout><DmcaPolicy /></Layout>} />
              <Route path="/data-request" element={<Layout><DataRequest /></Layout>} />
              <Route path="/affiliate-disclosure" element={<Layout><AffiliateDisclosure /></Layout>} />
              <Route path="/editorial-policy" element={<Layout><EditorialPolicy /></Layout>} />
              <Route path="/install" element={<Layout><Install /></Layout>} />

              {/* Public pages */}
              <Route path="/home" element={<Layout><Index /></Layout>} />
              <Route path="/about" element={<Layout><About /></Layout>} />
              <Route path="/recipes" element={<Layout><Recipes /></Layout>} />
              <Route path="/recipes/category/:slug" element={<Layout><CategoryHub /></Layout>} />
              <Route path="/recipe-index" element={<Layout><RecipeIndex /></Layout>} />
              <Route path="/search" element={<Layout><SearchResults /></Layout>} />
              {/* Dedicated print view — no nav/footer */}
              <Route path="/print/:slug" element={<PrintRecipe />} />
              <Route path="/shop" element={<Layout><Shop /></Layout>} />
              <Route path="/start-here" element={<Layout><StartHere /></Layout>} />
              <Route path="/cookbooks" element={<Layout><Cookbooks /></Layout>} />
              <Route path="/blog" element={<Layout><Blog /></Layout>} />
              <Route path="/blog/:slug" element={<Layout><BlogPost /></Layout>} />
              <Route path="/recipes/:slug" element={<Layout><RecipeDetail /></Layout>} />
              <Route path="/events" element={<Layout><Events /></Layout>} />
              <Route path="/press" element={<Layout><Press /></Layout>} />
              <Route path="/partnerships" element={<Navigate to="/contact?tab=partnerships" replace />} />
              <Route path="/contact" element={<Layout><Contact /></Layout>} />
              
              <Route path="/collections/all" element={<Navigate to="/" replace />} />
              <Route path="/rss" element={<Layout><RssFeed /></Layout>} />
              <Route path="/rss.xml" element={<RssRedirect />} />
              <Route path="/subscribe" element={<Layout><Subscribe /></Layout>} />
              <Route path="/notify" element={<Layout><RecipeAlerts /></Layout>} />
              <Route path="/recipe-request" element={<Layout><RecipeRequest /></Layout>} />
              <Route path="/walmartstorefront" element={<WalmartRedirect />} />

              {/* Cookbook QR video pages — no Layout, no nav, noindex */}
              <Route path="/cb/:slug" element={<CookbookVideo />} />

              {/* Admin */}
              <Route path="/daddy" element={<AdminLogin />} />
              <Route path="/daddy" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="inbox" element={<AdminInbox />} />
                <Route path="recipes" element={<AdminRecipes />} />
                <Route path="recipes/bulk-import" element={<AdminRecipeBulkImport />} />
                <Route path="recipes/:id" element={<AdminRecipeEditor />} />
                <Route path="cover-maker" element={<AdminCoverMaker />} />
                <Route path="blog" element={<AdminBlogPosts />} />
                <Route path="blog/bulk-import" element={<AdminBlogBulkImport />} />
                <Route path="blog/:id" element={<AdminBlogEditor />} />
                <Route path="calendar" element={<AdminCalendar />} />
                <Route path="health" element={<AdminHealthCheck />} />
                <Route path="media" element={<AdminMedia />} />
                <Route path="events" element={<AdminEvents />} />
                <Route path="press" element={<AdminPress />} />
                <Route path="inquiries" element={<AdminInquiries />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="comments" element={<AdminComments />} />
                <Route path="reviews" element={<AdminReviews />} />
                <Route path="links" element={<AdminLinks />} />
                <Route path="newsletter" element={<AdminNewsletterPreview />} />
                <Route path="contact" element={<AdminContactSubmissions />} />
                <Route path="recipe-alerts" element={<AdminRecipeAlerts />} />
                <Route path="signup-attempts" element={<AdminSignupAttempts />} />
                <Route path="recipe-requests" element={<AdminRecipeRequests />} />
                <Route path="tour-interest" element={<AdminTourInterest />} />
                <Route path="shop" element={<AdminShop />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="seo-health" element={<AdminSeoHealth />} />
                <Route path="backlinks" element={<AdminBacklinks />} />
                <Route path="cookbook-videos" element={<AdminCookbookVideos />} />
                <Route path="cookbook-videos/:id" element={<AdminCookbookVideoEditor />} />
              </Route>

              <Route path="*" element={<Layout><NotFound /></Layout>} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
